import { useEffect, useState, useRef } from 'react';
import { apiService } from '../services/apiService';
import { Link } from 'react-router-dom';
import ResponsiveTeamName from '../templates/ResponsiveTeamName';
import TeamLogo from '../templates/TeamLogo';
import HelperFunctions from '../helpers/HelperFunctions';


// `fixtureData` takes the raw next-round-fixtures payload (rounds[].fixtures[]);
// `fixtures` takes an already-flattened list, which is how callers feed the ticker a
// multi-league set merged from several competitions. Either one makes the ticker
// controlled, so it skips its own single-competition fetch.
// `linkTo` sends every ticker match to one fixed destination instead of its own fixture
// preview. The paid landing pages use it to keep all hero clicks on the sign-up funnel
// (see DemandGenLandingPage.js); everywhere else the per-fixture link is the default.
const LiveTicker = ({ onError, fixtureData, fixtures: fixturesProp, secondsPerFixture, predictionsText = 'Predictions Live', linkTo = null }) => {
    const [localFixtures, setLocalFixtures] = useState([]);
    const hasFetched = useRef(false);
    const { slugifyForUrl } = HelperFunctions();
    const isControlled = !!fixturesProp || !!fixtureData;

    useEffect(() => {
        if (isControlled || hasFetched.current) return;
        hasFetched.current = true;

        apiService
            .apiGet(import.meta.env.VITE_API_URL + "/competition/" + import.meta.env.VITE_CURRENT_COMPETITION_ID + "/next-round-fixtures")
            .then(
                (data) => {
                    const allFixtures = data?.rounds?.flatMap(round => round.fixtures || []) || [];
                    setLocalFixtures(allFixtures);
                },
                (error) => onError?.(error)
            );
    }, [onError, isControlled]);

    const fixtures = fixturesProp
        ? fixturesProp
        : fixtureData
            ? fixtureData.rounds?.flatMap(round => round.fixtures || []) || []
            : localFixtures;

    if (fixtures.length === 0) return null;

    // The marquee's CSS duration is fixed, so a shorter list covers less distance in
    // the same time and visibly crawls. Callers whose list length changes at runtime
    // (e.g. a league filter) pass secondsPerFixture to hold the scroll speed constant.
    const trackStyle = secondsPerFixture
        ? { animationDuration: `${fixtures.length * secondsPerFixture}s` }
        : undefined;

    return (
        <div style={{ position: 'relative', zIndex: 2 }}>
            <div className="ticker-track py-2" style={trackStyle}>
                {[...fixtures, ...fixtures].map((fixture, index) => (
                    <Link
                        key={index}
                        to={linkTo ?? `/fixtures-preview/${fixture.fixture_id}/${slugifyForUrl(`${fixture.home_team?.name || ''} vs ${fixture.away_team?.name || ''}`)}`}
                        className="ticker-match text-decoration-none"
                    >
                        <TeamLogo team={fixture.home_team} alt={fixture.home_team?.name} className="object-fit-contain rounded-1 flex-shrink-0 disc-45" />
                        <span className="ticker-team"><ResponsiveTeamName team={fixture.home_team} maxSize="shortened" minSize="shortened" /></span>
                        <span className="ticker-vs">vs</span>
                        <span className="ticker-team"><ResponsiveTeamName team={fixture.away_team} maxSize="shortened" minSize="shortened" /></span>
                        <TeamLogo team={fixture.away_team} alt={fixture.away_team?.name} className="object-fit-contain rounded-1 flex-shrink-0 disc-45" />
                        <span className="ticker-badge"><span className="dot"></span> {predictionsText}</span>

                    </Link>
                ))}
            </div>
        </div>
    );
};

export default LiveTicker;
